import api from './api';
import { Project, CreateProjectRequest, UpdateProjectRequest } from '../types/project.types';

interface RawProject {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  archetype?: string;
  preview_url?: string;
  sandbox_status: string;
  sandbox_built_at?: string;
  suggested_archetype?: string;
  suggested_rung?: string;
  router_confidence?: number;
  router_rationale?: string;
  router_clarified: boolean;
  created_at: string;
  updated_at: string;
}

function normalize(raw: RawProject): Project {
  return {
    id: raw.id,
    userId: raw.user_id,
    name: raw.name,
    description: raw.description,
    archetype: (raw.archetype as Project['archetype']) || 'fullstack',
    previewUrl: raw.preview_url,
    sandboxStatus: raw.sandbox_status as Project['sandboxStatus'],
    sandboxBuiltAt: raw.sandbox_built_at,
    suggestedArchetype: raw.suggested_archetype as Project['suggestedArchetype'],
    suggestedRung: raw.suggested_rung,
    routerConfidence: raw.router_confidence,
    routerRationale: raw.router_rationale,
    routerClarified: raw.router_clarified,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export const projectService = {
  async getProjects(): Promise<Project[]> {
    const response = await api.get<{ projects: RawProject[] }>('/api/projects');
    return response.data.projects.map(normalize);
  },

  async getProject(projectId: string): Promise<Project> {
    const response = await api.get<{ project: RawProject }>(`/api/projects/${projectId}`);
    return normalize(response.data.project);
  },

  async createProject(data: CreateProjectRequest): Promise<Project> {
    const response = await api.post<{ project: RawProject }>('/api/projects', data);
    return normalize(response.data.project);
  },

  async updateProject(projectId: string, data: UpdateProjectRequest): Promise<Project> {
    const response = await api.patch<{ project: RawProject }>(`/api/projects/${projectId}`, data);
    return normalize(response.data.project);
  },

  async deleteProject(projectId: string): Promise<void> {
    await api.delete(`/api/projects/${projectId}`);
  },

  async triggerBuild(projectId: string): Promise<void> {
    await api.post(`/api/projects/${projectId}/sandbox`);
  },

  async getSandboxState(projectId: string): Promise<{ status: string; last_error?: string }> {
    const response = await api.get<{ status: string; last_error?: string }>(
      `/api/projects/${projectId}/sandbox`
    );
    return response.data;
  },
};
